import React from 'react';
import { ChevronRightIcon, XIcon } from '@heroicons/react/solid';

// Lightweight inline spinner matching the button's text colour so it feels
// native rather than bolted-on.  Sized to match the ChevronRightIcon.
function ButtonSpinner() {
  return (
    <svg
      className="animate-spin w-5 h-5 ml-2"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

export default function FormActionButtons({ 
  isEditing, 
  handleCancelEdit, 
  hasAnyValue, 
  requiresAllSubquestions = false, 
  completeValidation = null,
  allVisibleAnswered = false,
  isSaving = false,
}) {
  // For scale-type forms (BDEFS, WEISS, etc.) every subquestion is required.
  // In edit mode, all visible subquestions must be answered to prevent saving a
  // previously-complete question in a partial state (e.g. after unselecting an option).
  // In normal (first-pass) mode, only having at least one value is required to proceed
  // — the question simply won't count as "complete" in progress until all are filled.
  const canProceed = requiresAllSubquestions
    ? (completeValidation?.allAnswered || false)
    : isEditing
      ? allVisibleAnswered
      : hasAnyValue;

  const isDisabled = !canProceed || isSaving;

  // Determine the appropriate button text based on context
  const getButtonText = () => {
    if (isSaving) {
      return "Saving…";
    }
    if (isEditing) {
      return "Save Edits";
    } else if (requiresAllSubquestions) {
      if (completeValidation?.allAnswered) {
        return "Save & Continue";
      } else {
        return `Complete All Questions (${completeValidation?.answeredCount || 0}/${completeValidation?.totalCount || 0})`;
      }
    } else if (hasAnyValue) {
      return "Save & Continue";
    } else {
      return "Save & Continue";
    }
  };

  return (
    <div className="flex flex-col-reverse sm:flex-row w-full sm:w-auto gap-3 sm:justify-end">
    {isEditing && !isSaving && (
        <button
          type="button"
          aria-label="Cancel edits and discard changes"
          className="w-full sm:w-auto flex items-center justify-center py-2 px-4 rounded-md shadow-md bg-red-600 text-white whitespace-nowrap"
          onClick={handleCancelEdit}
        >
          <span className="font-bold">Cancel Edits</span>
          <XIcon className="w-5 h-5 ml-2" aria-hidden="true" />
        </button>
    )}
       <button
        type="submit"
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label={getButtonText()}
        aria-busy={isSaving}
        className={`w-full sm:w-auto flex items-center justify-center py-2 px-4 rounded-md shadow-md transition duration-100 whitespace-nowrap ${
          isDisabled
            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed border' 
            : 'bg-violet-900 text-white hover:bg-violet-800'
        }`}
      >
        <span className="font-bold">{getButtonText()}</span>
        {isSaving ? <ButtonSpinner /> : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
      </button>
    </div>
  );
}